// ============================================================
// active-window - helper para el filtro "Active only".
//
// Una session es "activa AHORA" si:
//   1. isLive === true (no tiene SESSION_END)
//   2. La ventana de trading [dailyStartTime, dailyEndTime] en ET wall-clock
//      incluye la hora actual ET.
//
// Soporta ventanas overnight: si dailyStartTime > dailyEndTime (e.g.
// "18:00"-"00:01"), la ventana envuelve medianoche y "active" significa
// now >= start OR now <= end.
//
// Sessions sin daily window (null) NO se consideran active — no podemos
// decidir y devolver true seria un falso positivo confuso.
//
// Espejado en el backend (services/sessions.ts) para el filtro server-side
// `?status=active`. Esta version se usa para filtrar listas que el backend
// ya devolvio (e.g. Live Monitor cards) sin re-fetch.
// ============================================================

export interface ActiveWindowLike {
  isLive: boolean;
  dailyStartTime: string | null;
  dailyEndTime: string | null;
}

/**
 * Hora actual ET en formato "HH:mm". Pura para testear con un Date fijo.
 */
export function currentHHmmEt(now: Date = new Date()): string {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${hh === '24' ? '00' : hh}:${mm}`;
}

/**
 * Devuelve true si la session esta activa AHORA segun su ventana de trading.
 */
export function isSessionActiveNow(s: ActiveWindowLike, now: Date = new Date()): boolean {
  if (!s.isLive) return false;
  if (!s.dailyStartTime || !s.dailyEndTime) return false;
  const nowHHmm = currentHHmmEt(now);
  const start = s.dailyStartTime;
  const end = s.dailyEndTime;
  if (start <= end) {
    // Window normal
    return start <= nowHHmm && nowHHmm <= end;
  }
  // Overnight: e.g. start="18:00", end="00:01"
  return nowHHmm >= start || nowHHmm <= end;
}
